/**
 * useListingPreferences(moduleSlug) — F12 PR 2 część 5 sprint 1.
 *
 * Per-user preferencje paska narzędziowego listingu (search columns, archived
 * toggle, sort). Self-only — backend wymusza scope sesji.
 *
 * Działanie:
 *  - `useQuery` GET `/api/listing-preferences/:moduleSlug` → `{ config: {...} | null }`.
 *  - `setConfig(partial)` aktualizuje cache **natychmiast** (optimistic) +
 *    debounsuje PUT (500ms). Ostatni klik wygrywa, network niech nadąża.
 *  - Defaults z `fallback` (drugi argument hooka) gdy backend zwróci `null`.
 *
 * Hook celowo nie udostępnia `isLoading` / `error` z mutacji — listing UI
 * nie powinno zacinać się gdy zapis preferencji zwolni; cache trzyma stan.
 *
 * Sprint 2 dorzuci klucze (visibleColumns, columnOrder, advancedFilters,
 * groupBy, savedViews) bez zmiany kontraktu — config jest passthrough po
 * stronie backendu i tutaj jest typowany jako `Record<string, unknown>`.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface ListingSort {
  field: string;
  order: 'asc' | 'desc';
}

export interface ListingPreferencesConfig {
  searchColumns?: string[];
  includeArchived?: boolean;
  sort?: ListingSort | null;
  // Sprint 5.2 dorzuci dalsze klucze. Passthrough.
  [k: string]: unknown;
}

interface ListingPreferencesResponse {
  config: ListingPreferencesConfig | null;
}

export const LISTING_PREFERENCES_KEY = (moduleSlug: string) =>
  ['listing-preferences', moduleSlug] as const;

const DEBOUNCE_MS = 500;

export function useListingPreferences(moduleSlug: string, fallback: ListingPreferencesConfig = {}) {
  const queryClient = useQueryClient();
  const queryKey = LISTING_PREFERENCES_KEY(moduleSlug);

  const queryResult = useQuery<ListingPreferencesResponse>({
    queryKey,
    queryFn: async () => {
      const res = await api.get<ListingPreferencesResponse>(`/listing-preferences/${moduleSlug}`);
      return res.data;
    },
    staleTime: 60_000,
  });

  // Snapshot ostatniej wartości którą chcemy zapisać — debouncer odczyta z ref-a
  // żeby uniknąć stale closure (klik 1 → klik 2 → klik 3 → PUT widzi 3).
  const pendingRef = useRef<ListingPreferencesConfig | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(async () => {
    timerRef.current = null;
    const next = pendingRef.current;
    if (!next) return;
    pendingRef.current = null;
    try {
      await api.put(`/listing-preferences/${moduleSlug}`, { config: next });
    } catch {
      // Cichy fail — preferences UX, brak toast spamu. Cache zostaje
      // optimistic; przy następnym GET (cache invalidation) wróci do bazy.
    }
  }, [moduleSlug]);

  // Cleanup: gdy komponent znika z aktywnym timerem — pchnij od razu.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        void flush();
      }
    };
  }, [flush]);

  const config = useMemo<ListingPreferencesConfig>(() => {
    return queryResult.data?.config ?? fallback;
    // `fallback` celowo NIE w deps — caller przekazuje literal i obiekt zmienia
    // referencję per render, nie chcemy infinite refetch. Wystarczy że nowy
    // moduleSlug rzuci nową odpowiedź z queryFn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResult.data?.config]);

  const setConfig = useCallback(
    (partial: Partial<ListingPreferencesConfig>) => {
      // Optimistic — zaktualizuj cache od razu (UI re-renderuje natychmiast).
      const prev =
        queryClient.getQueryData<ListingPreferencesResponse>(queryKey)?.config ?? fallback;
      const next: ListingPreferencesConfig = { ...prev, ...partial };
      queryClient.setQueryData<ListingPreferencesResponse>(queryKey, { config: next });

      pendingRef.current = next;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void flush();
      }, DEBOUNCE_MS);
    },
    // `fallback` jak wyżej — celowo nie w deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, queryKey, flush],
  );

  return {
    config,
    setConfig,
    isLoading: queryResult.isLoading,
  };
}
